import { isEmpty } from 'lodash'

import { apiService, fetchingAPI } from '@/api'

import { toast } from '@/components/toasts/toast.helpers'
import { CHANNEL_TYPE } from '@/utils/constants'
import * as redux from './baltoGPT.redux'
import { MAX_CHAT_HISTORY } from './baltoGPT.redux'

export const searchCallsWithBaltoGPT = (values) => {
  return async (dispatch, getState) => {
    const { calls } = getState().callSearch
    const { chatFeed } = getState().baltoGPT

    dispatch(redux.setLoading({ baltoGPTChat: true }))
    dispatch(redux.updateChatFeed({ chatType: 'agent', text: values.prompt }))

    // If call_ids were passed from the component, use those. Otherwise, fall back to calls from Redux
    // This allows the component to work both with playlist calls and call explorer search results
    const callIds =
      Array.isArray(values.call_ids) && values.call_ids.length > 0
        ? values.call_ids.filter((id) => id != null)
        : Array.isArray(calls)
          ? calls.map((call) => call.call_id).filter((id) => id != null)
          : []

    let conversationHistory = [
      ...chatFeed.map((msg) => ({
        role: msg.chatType === 'agent' ? 'user' : 'assistant',
        content: msg.text,
      })),
      { role: 'user', content: values.prompt },
    ]
    if (conversationHistory.length > MAX_CHAT_HISTORY) {
      conversationHistory = conversationHistory.slice(-MAX_CHAT_HISTORY)
    }

    const valuesWithCallIds = {
      ...values,
      call_ids: callIds,
      conversation_history: conversationHistory,
    }

    if (isEmpty(valuesWithCallIds.call_ids)) {
      toast.error('Please make a call explorer search before using BaltoGPT')
      dispatch(redux.setLoading({ baltoGPTChat: false }))
      return
    }

    try {
      const response = await fetchingAPI(
        `${apiService.summary}/beacon/search`,
        'POST',
        dispatch,
        JSON.stringify(valuesWithCallIds)
      )

      dispatch(redux.updateChatFeed({ chatType: 'baltoGPT', text: response?.response }))
    } catch (err) {
      dispatch(
        redux.updateChatFeed({
          chatType: 'error',
          text: 'Failed to search calls with BaltoGPT. Try updating your search range or call duration.',
        })
      )
    } finally {
      dispatch(redux.setLoading({ baltoGPTChat: false }))
    }
  }
}

export const searchTranscriptWithBaltoGPT = (values) => {
  return async (dispatch, getState) => {
    const { callExplorer } = getState().callSearch
    const { messageThread } = getState().messages
    const { chatFeed } = getState().baltoGPT

    dispatch(redux.setLoading({ baltoGPTChat: true }))
    dispatch(redux.updateChatFeed({ chatType: 'agent', text: values.prompt }))

    const updatedChatFeed = [...chatFeed, { chatType: 'agent', text: values.prompt }]

    const isMessageThread = !isEmpty(messageThread)
    const itemType = isMessageThread ? CHANNEL_TYPE.EMAIL : CHANNEL_TYPE.CALL

    if (!isMessageThread && isEmpty(callExplorer?.transcript)) {
      toast.error(`No transcript is available for this ${itemType}`)
      dispatch(redux.setLoading({ baltoGPTChat: false }))
      return
    }
    const lastCallId = isMessageThread
      ? messageThread
          .map((msg) => msg.call_id)
          .filter((id) => Number.isInteger(id))
          .at(-1)
      : null
    if (isMessageThread && !lastCallId) {
      toast.error('No valid call ID found in the message thread')
      dispatch(redux.setLoading({ baltoGPTChat: false }))
      return
    }

    const endpoint = isMessageThread
      ? `${apiService.summary}/beacon/search/${lastCallId}`
      : `${apiService.summary}/beacon/search/${callExplorer.callId}`

    const payload = {
      prompt: values.prompt,
      conversation_history: updatedChatFeed.map((msg) => ({
        role: msg.chatType === 'agent' ? 'user' : 'assistant',
        content: msg.text,
      })),
    }
    try {
      const response = await fetchingAPI(endpoint, 'POST', dispatch, JSON.stringify(payload))
      dispatch(redux.updateChatFeed({ chatType: 'baltoGPT', text: response?.response }))
    } catch (err) {
      dispatch(
        redux.updateChatFeed({
          chatType: 'error',
          text: 'Failed to search transcript with BaltoGPT',
        })
      )
    } finally {
      dispatch(redux.setLoading({ baltoGPTChat: false }))
    }
  }
}

export const searchTrendsWithBaltoGPT = (values, signal) => {
  return async (dispatch, getState) => {
    const { chatFeed } = getState().baltoGPT

    dispatch(redux.setLoading({ baltoGPTChat: true }))
    dispatch(redux.updateChatFeed({ chatType: 'agent', text: values.prompt }))

    const callIds = Array.isArray(values.call_ids) ? values.call_ids.filter((id) => id != null) : []

    let conversationHistory = [
      ...chatFeed.map((msg) => ({
        role: msg.chatType === 'agent' ? 'user' : 'assistant',
        content: msg.text,
      })),
      { role: 'user', content: values.prompt },
    ]
    if (conversationHistory.length > MAX_CHAT_HISTORY) {
      conversationHistory = conversationHistory.slice(-MAX_CHAT_HISTORY)
    }

    const payload = {
      call_ids: callIds,
      prompt: values.prompt,
      conversation_history: conversationHistory,
      trend_context: values.trend_context,
    }

    if (isEmpty(payload.call_ids)) {
      toast.error('No calls available to analyze')
      dispatch(redux.setLoading({ baltoGPTChat: false }))
      return
    }

    try {
      const response = await fetchingAPI(
        `${apiService.summary}/beacon/search/trends`,
        'POST',
        dispatch,
        JSON.stringify(payload),
        undefined,
        undefined,
        undefined,
        signal
      )

      if (response?.response) {
        dispatch(redux.updateChatFeed({ chatType: 'baltoGPT', text: response.response }))
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return
      }
      dispatch(
        redux.updateChatFeed({
          chatType: 'error',
          text: 'Failed to analyze trend data with BaltoGPT. Please try again.',
        })
      )
    } finally {
      dispatch(redux.setLoading({ baltoGPTChat: false }))
    }
  }
}

export const searchInsightWithBaltoGPT = (insightId, values, signal) => {
  return async (dispatch, getState) => {
    const { chatFeed } = getState().baltoGPT

    dispatch(redux.setLoading({ baltoGPTChat: true }))
    dispatch(redux.updateChatFeed({ chatType: 'agent', text: values.prompt }))

    let conversationHistory = [
      ...chatFeed.map((msg) => ({
        role: msg.chatType === 'agent' ? 'user' : 'assistant',
        content: msg.text,
      })),
      { role: 'user', content: values.prompt },
    ]
    if (conversationHistory.length > MAX_CHAT_HISTORY) {
      conversationHistory = conversationHistory.slice(-MAX_CHAT_HISTORY)
    }

    const payload = {
      prompt: values.prompt,
      conversation_history: conversationHistory,
    }

    try {
      const response = await fetchingAPI(
        `${apiService.summary}/beacon/search/insights/${insightId}`,
        'POST',
        dispatch,
        JSON.stringify(payload),
        undefined,
        undefined,
        undefined,
        signal
      )

      if (response?.response) {
        dispatch(redux.updateChatFeed({ chatType: 'baltoGPT', text: response.response }))
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return
      }
      dispatch(
        redux.updateChatFeed({
          chatType: 'error',
          text: 'Failed to chat about this insight. Please try again.',
        })
      )
    } finally {
      dispatch(redux.setLoading({ baltoGPTChat: false }))
    }
  }
}

// Chat over a whole pipeline run (the Explore tab's ask bar). Same shape as
// searchInsightWithBaltoGPT, but the entry point is the run, so the backend
// assembles run-wide context instead of a single insight's.
export const searchRunWithBaltoGPT = (runId, values, signal) => {
  return async (dispatch, getState) => {
    const { chatFeed } = getState().baltoGPT

    dispatch(redux.setLoading({ baltoGPTChat: true }))
    dispatch(redux.updateChatFeed({ chatType: 'agent', text: values.prompt }))

    let conversationHistory = [
      ...chatFeed.map((msg) => ({
        role: msg.chatType === 'agent' ? 'user' : 'assistant',
        content: msg.text,
      })),
      { role: 'user', content: values.prompt },
    ]
    if (conversationHistory.length > MAX_CHAT_HISTORY) {
      conversationHistory = conversationHistory.slice(-MAX_CHAT_HISTORY)
    }

    const payload = {
      prompt: values.prompt,
      conversation_history: conversationHistory,
    }

    try {
      const response = await fetchingAPI(
        `${apiService.summary}/beacon/search/runs/${runId}`,
        'POST',
        dispatch,
        JSON.stringify(payload),
        undefined,
        undefined,
        undefined,
        signal
      )

      if (response?.response) {
        dispatch(redux.updateChatFeed({ chatType: 'baltoGPT', text: response.response }))
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return
      }
      dispatch(
        redux.updateChatFeed({
          chatType: 'error',
          text: 'Failed to chat about these calls. Please try again.',
        })
      )
    } finally {
      dispatch(redux.setLoading({ baltoGPTChat: false }))
    }
  }
}

export const resetChatFeed = () => {
  return async (dispatch) => {
    dispatch(redux.reset())
  }
}
